import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import Shell from "@/components/Shell";
import {
    Inbox, Send, Copy, Check, Trash2, RefreshCw,
    ArrowDownToLine, FileText, Plus, Loader2,
} from "lucide-react";

// COMMS — the relay between No. 2 (Claude / handler) and No. 3 (Emergent / builder).
// Six is the courier. Inbound = briefings from Two pasted at session start.
// Outbound = Session Summary filed at session end, copy-paste back to Two.

function StampHeader({ kicker, title, sub }) {
    return (
        <div className="mb-6">
            <div className="label-typed tw-prefix">{kicker}</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                {title}<span className="cursor-blink" />
            </h1>
            {sub && <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">{sub}</p>}
        </div>
    );
}

function CopyButton({ text, testid }) {
    const [done, setDone] = useState(false);
    const onClick = async () => {
        if (!text) return;
        try { await navigator.clipboard.writeText(text); } catch { /* no-op */ }
        setDone(true);
        setTimeout(() => setDone(false), 1500);
    };
    return (
        <button
            onClick={onClick}
            disabled={!text}
            data-testid={testid}
            className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-3 py-1.5 font-heading uppercase text-[10px] tracking-[0.25em] disabled:opacity-40"
        >
            {done ? <Check className="w-3 h-3 text-stamp-green" /> : <Copy className="w-3 h-3" />}
            {done ? "Copied" : "Copy"}
        </button>
    );
}

function ListEditor({ label, items, setItems, placeholder, testid }) {
    const [draft, setDraft] = useState("");
    const add = () => {
        const v = draft.trim();
        if (!v) return;
        setItems([...items, v]);
        setDraft("");
    };
    return (
        <div className="paper-card p-4" data-testid={`${testid}-block`}>
            <div className="flex items-center justify-between mb-2">
                <div className="label-typed text-stamp-red">{label}</div>
                <span className="font-body text-[10px] text-ink-soft tracking-[0.18em] uppercase">
                    {items.length} filed
                </span>
            </div>
            <div className="flex gap-2 mb-3">
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder={placeholder}
                    data-testid={`${testid}-input`}
                    className="flex-1 bg-paper-light/60 border border-ink/20 px-3 py-1.5 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
                />
                <button
                    onClick={add}
                    data-testid={`${testid}-add`}
                    className="inline-flex items-center gap-1 bg-ink text-paper-light hover:bg-stamp-red px-3 py-1.5 font-heading uppercase text-[10px] tracking-[0.25em]"
                >
                    <Plus className="w-3 h-3" /> Add
                </button>
            </div>
            {items.length === 0 ? (
                <div className="font-body text-xs text-ink-faint italic">— nothing filed —</div>
            ) : (
                <ul className="space-y-1.5">
                    {items.map((it, i) => (
                        <li
                            key={i}
                            className="flex items-start justify-between gap-2 group"
                            data-testid={`${testid}-item-${i}`}
                        >
                            <span className="font-body text-sm text-ink">• {it}</span>
                            <button
                                onClick={() => setItems(items.filter((_, j) => j !== i))}
                                className="text-ink-faint group-hover:text-stamp-red"
                                aria-label="remove"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function InboundPanel({ refreshKey, onChange }) {
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [items, setItems] = useState([]);
    const [openId, setOpenId] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/comms/inbound");
            setItems(data.items || []);
        } catch (e) {
            // surface but don't block
            setErr(`[HTTP ${e.response?.status || "no-response"}] ${e.response?.data?.detail || e.message}`);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

    const submit = async () => {
        if (!body.trim()) { setErr("Paste the briefing first."); return; }
        setBusy(true); setErr("");
        try {
            await api.post("/comms/inbound", {
                title: title.trim() || null,
                body_md: body,
                source_agent: "Two",
            });
            setTitle(""); setBody("");
            await load();
            onChange?.();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "no-response"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/comms/inbound/${id}`);
            await load();
            if (openId === id) setOpenId(null);
            onChange?.();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
        }
    };

    const open = items.find((x) => x.id === openId);

    return (
        <section className="space-y-4" data-testid="comms-inbound-panel">
            <div className="paper-card p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Inbox className="w-4 h-4 text-stamp-red" />
                        <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink">
                            Inbound · From No. 2
                        </div>
                    </div>
                    <span className="classified-stamp text-[10px]">EYES ONLY</span>
                </div>
                <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder='Title (optional — auto-derived from first heading)'
                    data-testid="comms-inbound-title"
                    className="w-full bg-paper-light/60 border border-ink/20 px-3 py-2 mb-2 font-heading uppercase tracking-[0.18em] text-sm text-ink placeholder:text-ink-faint placeholder:normal-case placeholder:tracking-normal focus:outline-none focus:border-stamp-red"
                />
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Paste the briefing from No. 2 here, top to bottom. Markdown welcome. Original is filed verbatim — never altered."
                    data-testid="comms-inbound-body"
                    rows={10}
                    className="w-full bg-paper-light/60 border border-ink/20 px-3 py-2 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red leading-relaxed"
                />
                <div className="flex items-center justify-between mt-3">
                    <div className="label-typed">
                        {body ? `${body.length.toLocaleString()} characters` : "—"}
                    </div>
                    <button
                        onClick={submit}
                        disabled={busy || !body.trim()}
                        data-testid="comms-inbound-submit"
                        className="inline-flex items-center gap-2 bg-ink text-paper-light hover:bg-stamp-red px-5 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowDownToLine className="w-3 h-3" />}
                        File briefing
                    </button>
                </div>
                {err && (
                    <div className="mt-3 border-2 border-stamp-red p-3" data-testid="comms-inbound-error">
                        <div className="flex items-center justify-between mb-1">
                            <span className="stamp-red font-heading text-[11px] tracking-[0.2em]">FAILURE ON FILE</span>
                            <button onClick={() => setErr("")} className="font-heading uppercase text-[10px] tracking-[0.2em] text-ink-soft hover:text-stamp-red">clear</button>
                        </div>
                        <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words">{err}</pre>
                    </div>
                )}
            </div>

            <div className="paper-card p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink">
                        Briefing archive
                    </div>
                    <button
                        onClick={load}
                        data-testid="comms-inbound-refresh"
                        className="inline-flex items-center gap-1 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-2 py-1 font-heading uppercase text-[10px] tracking-[0.22em]"
                    >
                        <RefreshCw className="w-3 h-3" />
                    </button>
                </div>
                {items.length === 0 ? (
                    <div className="font-body text-sm text-ink-faint italic">No inbound briefings filed yet.</div>
                ) : (
                    <ul className="divide-y divide-ink/10">
                        {items.map((it) => (
                            <li key={it.id} className="py-3" data-testid={`comms-inbound-row-${it.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <button
                                        onClick={() => setOpenId(openId === it.id ? null : it.id)}
                                        className="text-left flex-1"
                                    >
                                        <div className="font-heading text-sm text-ink uppercase tracking-[0.06em]">
                                            {it.title}
                                        </div>
                                        <div className="label-typed mt-1">
                                            {it.received_at?.replace("T", " ").slice(0, 19)} · {it.source_agent} · {(it.byte_count / 1024).toFixed(1)} KB
                                        </div>
                                    </button>
                                    <div className="flex items-center gap-2">
                                        <CopyButton text={it.body_md} testid={`comms-inbound-copy-${it.id}`} />
                                        <button
                                            onClick={() => remove(it.id)}
                                            className="border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-2 py-1.5"
                                            data-testid={`comms-inbound-delete-${it.id}`}
                                            aria-label="delete"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                                {openId === it.id && (
                                    <pre className="mt-3 max-h-96 overflow-auto bg-paper-light/40 border border-ink/15 p-3 font-body text-xs text-ink whitespace-pre-wrap break-words" data-testid={`comms-inbound-body-${it.id}`}>
                                        {it.body_md}
                                    </pre>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

function OutboundPanel({ refreshKey, onChange }) {
    const [completed, setCompleted] = useState([]);
    const [inProgress, setInProgress] = useState([]);
    const [blockers, setBlockers] = useState([]);
    const [rfi, setRfi] = useState([]);
    const [notes, setNotes] = useState([]);
    const [draft, setDraft] = useState(null);
    const [latest, setLatest] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [history, setHistory] = useState([]);
    const [openId, setOpenId] = useState(null);

    const load = async () => {
        try {
            const [d, list] = await Promise.all([
                api.get("/comms/draft-summary"),
                api.get("/comms/outbound"),
            ]);
            setDraft(d.data);
            setHistory(list.data.items || []);
            setLatest(list.data.items?.[0] || null);
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "no-response"}] ${e.response?.data?.detail || e.message}`);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

    const file = async () => {
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/comms/outbound", {
                tasks_completed: completed,
                tasks_in_progress: inProgress,
                blockers,
                rfi_updates: rfi,
                notes_for_two: notes,
                target_agent: "Two",
            });
            setLatest(data);
            setCompleted([]); setInProgress([]); setBlockers([]); setRfi([]); setNotes([]);
            await load();
            onChange?.();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "no-response"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/comms/outbound/${id}`);
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
        }
    };

    const preview = useMemo(() => {
        const date = draft?.date || new Date().toISOString().slice(0, 10);
        const blk = (n, arr) => arr.length === 0 ? `${n}: —` : `${n}:\n` + arr.map(x => `  - ${x}`).join("\n");
        return [
            "SESSION SUMMARY — Agent Three",
            `Date: ${date}`,
            blk("Tasks completed", completed),
            blk("Tasks in progress", inProgress),
            blk("Blockers", blockers),
            blk("RFI updates (add/close/amend)", rfi),
            blk("Notes for Agent Two", notes),
            "",
            "Be seeing you. — Agent Three / Earnie",
        ].join("\n");
    }, [completed, inProgress, blockers, rfi, notes, draft]);

    return (
        <section className="space-y-4" data-testid="comms-outbound-panel">
            <div className="paper-card p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Send className="w-4 h-4 text-stamp-red" />
                        <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink">
                            Outbound · To No. 2
                        </div>
                    </div>
                    <span className="classified-stamp text-[10px]">SESSION SUMMARY</span>
                </div>

                {draft && (
                    <div className="mb-4 border border-ink/15 bg-paper-light/40 p-3" data-testid="comms-stats-block">
                        <div className="label-typed mb-2">Auto-stats · drawn from the Shed at {new Date().toLocaleTimeString()}</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-body text-xs text-ink">
                            <div>
                                <div className="label-typed">Today's entries</div>
                                <div className="font-heading text-lg">{draft.stats.ledger_entries_today}</div>
                            </div>
                            <div>
                                <div className="label-typed">Total ledger</div>
                                <div className="font-heading text-lg">{draft.stats.ledger_entries_total}</div>
                            </div>
                            <div>
                                <div className="label-typed">Tracks today</div>
                                <div className="font-heading text-lg">{draft.stats.radio_tracks_today}</div>
                            </div>
                            <div>
                                <div className="label-typed">Last inbound</div>
                                <div className="font-heading text-xs leading-tight">
                                    {draft.stats.latest_inbound_briefing
                                        ? draft.stats.latest_inbound_briefing.received_at?.slice(0, 10)
                                        : "—"}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-3">
                    <ListEditor label="Tasks completed" items={completed} setItems={setCompleted} placeholder="What did this session ship?" testid="comms-tasks-completed" />
                    <ListEditor label="Tasks in progress" items={inProgress} setItems={setInProgress} placeholder="What carries to the next session?" testid="comms-tasks-in-progress" />
                    <ListEditor label="Blockers" items={blockers} setItems={setBlockers} placeholder="What stopped you, and where?" testid="comms-blockers" />
                    <ListEditor label="RFI updates" items={rfi} setItems={setRfi} placeholder='e.g. "THREE-008 closed: Mureka live"' testid="comms-rfi" />
                </div>
                <div className="mt-3">
                    <ListEditor label="Notes for Agent Two" items={notes} setItems={setNotes} placeholder="Anything Two should know before next session." testid="comms-notes" />
                </div>

                <div className="mt-4">
                    <div className="label-typed mb-1">Preview · what gets filed and copied</div>
                    <pre className="bg-paper-light/40 border border-ink/15 p-3 font-body text-xs text-ink whitespace-pre-wrap break-words max-h-72 overflow-auto" data-testid="comms-outbound-preview">
                        {preview}
                    </pre>
                </div>

                <div className="flex items-center justify-between mt-3">
                    <div className="label-typed">{(completed.length + inProgress.length + blockers.length + rfi.length + notes.length)} entries on the page</div>
                    <div className="flex items-center gap-2">
                        <CopyButton text={preview} testid="comms-outbound-copy-preview" />
                        <button
                            onClick={file}
                            disabled={busy}
                            data-testid="comms-outbound-submit"
                            className="inline-flex items-center gap-2 bg-ink text-paper-light hover:bg-stamp-red px-5 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            File summary
                        </button>
                    </div>
                </div>
                {err && (
                    <div className="mt-3 border-2 border-stamp-red p-3" data-testid="comms-outbound-error">
                        <div className="flex items-center justify-between mb-1">
                            <span className="stamp-red font-heading text-[11px] tracking-[0.2em]">FAILURE ON FILE</span>
                            <button onClick={() => setErr("")} className="font-heading uppercase text-[10px] tracking-[0.2em] text-ink-soft hover:text-stamp-red">clear</button>
                        </div>
                        <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words">{err}</pre>
                    </div>
                )}
            </div>

            <div className="paper-card p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink">
                        Filed summaries
                    </div>
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-1 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-2 py-1 font-heading uppercase text-[10px] tracking-[0.22em]"
                    >
                        <RefreshCw className="w-3 h-3" />
                    </button>
                </div>
                {history.length === 0 ? (
                    <div className="font-body text-sm text-ink-faint italic">No outbound summaries filed yet.</div>
                ) : (
                    <ul className="divide-y divide-ink/10">
                        {history.map((it) => (
                            <li key={it.id} className="py-3" data-testid={`comms-outbound-row-${it.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <button
                                        onClick={() => setOpenId(openId === it.id ? null : it.id)}
                                        className="text-left flex-1"
                                    >
                                        <div className="font-heading text-sm text-ink uppercase tracking-[0.06em]">
                                            <FileText className="w-3 h-3 inline mr-1" />
                                            {it.date} · summary {it.id.slice(0, 8)}
                                        </div>
                                        <div className="label-typed mt-1">
                                            {it.filed_at?.replace("T", " ").slice(0, 19)} · → {it.target_agent}
                                        </div>
                                    </button>
                                    <div className="flex items-center gap-2">
                                        <CopyButton text={it.body_md} testid={`comms-outbound-copy-${it.id}`} />
                                        <button
                                            onClick={() => remove(it.id)}
                                            className="border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-2 py-1.5"
                                            data-testid={`comms-outbound-delete-${it.id}`}
                                            aria-label="delete"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                                {openId === it.id && (
                                    <pre className="mt-3 bg-paper-light/40 border border-ink/15 p-3 font-body text-xs text-ink whitespace-pre-wrap break-words" data-testid={`comms-outbound-body-${it.id}`}>
                                        {it.body_md}
                                    </pre>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

export default function Comms() {
    const [tick, setTick] = useState(0);
    const [normaliseBusy, setNormaliseBusy] = useState(false);
    const [normaliseResult, setNormaliseResult] = useState(null);
    const bump = () => setTick((t) => t + 1);

    // Iter 31.87 — admin one-shot canonical normalisation. Dry-run first
    // so the operator sees the counts before committing. Idempotent
    // server-side so a click on a clean DB is a safe no-op.
    const runNormalise = async (dryRun) => {
        setNormaliseBusy(true);
        setNormaliseResult(null);
        try {
            const url = `/comms/normalise-existing-rows${dryRun ? "?dry_run=true" : ""}`;
            const { data } = await api.post(url);
            setNormaliseResult({ ...data, was_dry: dryRun });
            if (!dryRun) bump();
        } catch (e) {
            setNormaliseResult({
                error: e?.response?.data?.detail || e.message || "Normalise failed",
            });
        } finally {
            setNormaliseBusy(false);
        }
    };

    return (
        <Shell>
            <StampHeader
                kicker="Two ↔ Three · Six is the courier"
                title="Comms"
                sub="The relay desk. Two pastes the briefing in. Three files the Session Summary out. Originals are kept verbatim — append-only, never altered. The day this page talks to itself, Six can take the morning off."
            />

            <div className="grid lg:grid-cols-2 gap-6" data-testid="comms-grid">
                <InboundPanel refreshKey={tick} onChange={bump} />
                <OutboundPanel refreshKey={tick} onChange={bump} />
            </div>

            {/* Iter 31.87 — Admin: normalise legacy target_agent / source_agent values */}
            <div className="mt-10 paper-card border border-dashed border-ink/30 p-4" data-testid="comms-admin">
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                    <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                        Admin · canonical normalisation
                    </h3>
                    <span className="label-typed text-[10px]">owner only · idempotent</span>
                </div>
                <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                    Walks every comms row and rewrites legacy non-canonical recipients
                    (<code className="text-ink">claude&nbsp;(Anthropic)</code>,&nbsp;
                    <code className="text-ink">two</code>,&nbsp;
                    <code className="text-ink">agent&nbsp;3</code>,&nbsp;
                    <code className="text-ink">Earnie</code>) to the Village name
                    (<code className="text-ink">Two</code>,&nbsp;
                    <code className="text-ink">Three</code>,&nbsp;
                    <code className="text-ink">Six</code>). Original value is preserved
                    in <code className="text-ink">target_agent_pre_normalise</code> /&nbsp;
                    <code className="text-ink">source_agent_pre_normalise</code> on each
                    rewritten row.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => runNormalise(true)}
                        disabled={normaliseBusy}
                        data-testid="comms-normalise-dryrun"
                        className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Dry-run
                    </button>
                    <button
                        onClick={() => {
                            if (window.confirm("Rewrite legacy comms target/source agent values to canonical Village names?\n\nOriginal values are preserved in *_pre_normalise fields. Idempotent — re-running is a no-op.")) {
                                runNormalise(false);
                            }
                        }}
                        disabled={normaliseBusy}
                        data-testid="comms-normalise-run"
                        className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {normaliseBusy ? "Working…" : "Normalise now"}
                    </button>
                </div>
                {normaliseResult && (
                    <div
                        className="mt-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3"
                        data-testid="comms-normalise-result"
                    >
                        {normaliseResult.error ? (
                            <span className="text-stamp-red">Error: {normaliseResult.error}</span>
                        ) : (
                            <>
                                <div>
                                    {normaliseResult.was_dry ? "Dry-run" : "Run"} complete
                                    {normaliseResult.dry_run ? " (no writes)" : ""}.
                                </div>
                                <div className="mt-1">
                                    Outbound: {normaliseResult.outbound.scanned} scanned ·{" "}
                                    <span className="text-stamp-red">{normaliseResult.outbound.updated} {normaliseResult.was_dry ? "would update" : "updated"}</span> ·{" "}
                                    {normaliseResult.outbound.skipped} skipped
                                </div>
                                <div>
                                    Inbound: {normaliseResult.inbound.scanned} scanned ·{" "}
                                    <span className="text-stamp-red">{normaliseResult.inbound.updated} {normaliseResult.was_dry ? "would update" : "updated"}</span> ·{" "}
                                    {normaliseResult.inbound.skipped} skipped
                                </div>
                                {normaliseResult.outbound.samples?.length > 0 && (
                                    <div className="mt-2 opacity-80">
                                        Samples (outbound):{" "}
                                        {normaliseResult.outbound.samples.slice(0, 3).map((s, i) => (
                                            <span key={s.id || i} className="mr-2">
                                                <code className="text-ink">{s.from}</code> → <code className="text-ink">{s.to}</code>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-10 hairline" />
            <div className="mt-4 label-typed text-center">
                Be seeing you · Two holds the file · Three builds the room · Six holds the keys
            </div>
        </Shell>
    );
}
